import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { usePinnedPresetRoutes, useTablePresets } from '@/entities/settings';
import { useAppNavigate } from '@/shared/lib';

/**
 * Держит согласованными раздел, открытый адресом, и активный пресет таблицы.
 *
 * Заход в раздел закреплённого пресета применяет его к списку. Адрес, которому не отвечает ни один
 * закреплённый пресет (открепление, удаление, переименование, ссылка в никуда), уводит в раздел
 * «Диалоги». Сам раздел «Диалоги» закреплённого пресета не держит: он там снимается с возвратом
 * отложенного личного вида — иначе раздел показывал бы отбор соседнего раздела.
 *
 * Живёт на уровне страницы, а не списка: в раскладках, где список рядом с открытой записью не
 * показан, связь адреса и пресета должна работать так же.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`); `null` — раздел без пресетов.
 */
export const usePresetSectionSync = (tableName: string | null) => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useAppNavigate();
  const pinnedRoutes = usePinnedPresetRoutes(tableName ?? '');
  const { activePresetId, apply, clearActive } = useTablePresets(tableName ?? '');

  // Адрес, пресет которого уже применён: применяем только на заходе. Смена активного пресета
  // при открытом адресе сама уводит с него, но новый адрес приходит отложенно — повторное
  // применение по ещё старому адресу откатило бы выбор пользователя.
  const appliedSlugRef = useRef<string | null>(null);

  useEffect(() => {
    // Раздел без пресетов адрес с ними не связывает — ни применения по заходу, ни снятия.
    if (!tableName) return;
    if (!slug) {
      appliedSlugRef.current = null;
      // Незакреплённый пресет — обычный отбор поверх списка диалогов, его раздел не снимает.
      if (pinnedRoutes.some((route) => route.preset.id === activePresetId)) {
        clearActive();
      }
      return;
    }

    const openedRoute = pinnedRoutes.find((route) => route.slug === slug);
    if (!openedRoute) {
      appliedSlugRef.current = null;
      navigate('/dialogs/:id?', { id: null }, { replace: true });
      return;
    }
    if (appliedSlugRef.current === slug) return;

    appliedSlugRef.current = slug;
    if (activePresetId !== openedRoute.preset.id) {
      apply(openedRoute.preset.id);
    }
  }, [tableName, slug, pinnedRoutes, activePresetId, apply, clearActive, navigate]);
};
